// Auth service for handling authentication API calls

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  accessToken?: string;
  username?: string;
  message?: string;
}

class AuthService {
  private baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3042'; // Backend API URL

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseURL}/v1/auth/sign-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
        credentials: 'include', // Để gửi cookies
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          accessToken: data.data.token,
          username: data.data.username,
        };
      } else {
        return {
          success: false,
          message: data.status?.message || 'Đăng nhập thất bại',
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi đăng nhập',
      };
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseURL}/v1/auth/sign-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
        credentials: 'include', // Để gửi cookies
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          message: 'Đăng ký thành công',
        };
      } else {
        return {
          success: false,
          message: data.status?.message || 'Đăng ký thất bại',
        };
      }
    } catch (error) {
      console.error('Register error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi đăng ký',
      };
    }
  }

  async validateToken(): Promise<AuthResponse> {
    try {
      const token = this.getAccessToken();
      if (!token) {
        return {
          success: false,
          message: 'No token found',
        };
      }

      // Gửi request với token trong Authorization header (fallback)
      // và credentials: 'include' để tự động gửi cookie
      // Middleware sẽ tự động đọc token từ cookie hoặc Authorization header
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Thêm Authorization header để đảm bảo token được gửi
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseURL}/v1/auth/validate-token`, {
        method: 'POST',
        headers: headers,
        credentials: 'include', // Quan trọng: để gửi cookie
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          username: data.data.username,
        };
      } else {
        // Token không hợp lệ, xóa cookie
        this.removeAccessToken();
        return {
          success: false,
          message: data.status?.message || 'Token validation failed',
        };
      }
    } catch (error) {
      console.error('Token validation error:', error);
      // Xóa token khi có lỗi
      this.removeAccessToken();
      return {
        success: false,
        message: 'Có lỗi xảy ra khi xác thực token',
      };
    }
  }

  async logout(): Promise<void> {
    try {
      // Backend không có endpoint logout riêng, chỉ cần xóa token ở client
      // Nếu cần gọi API logout, có thể thêm endpoint tương ứng ở backend
      console.log('User logged out');
    } catch (error) {
      console.log('Logout error:', error);
    }
  }

  async forgotPassword(username: string): Promise<{ success: boolean; email?: string; message?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/v1/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          email: data.data.email,
          message: data.data.message,
        };
      } else {
        return {
          success: false,
          message: data.status?.message || 'Có lỗi xảy ra',
        };
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi gửi mã xác thực',
      };
    }
  }

  async resetPassword(username: string, pin: string, password: string): Promise<{ success: boolean; message?: string; code?: number }> {
    try {
      const response = await fetch(`${this.baseURL}/v1/auth/forgot-change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, pin, password }),
        credentials: 'include',
      });

      const data = await response.json();
      // Support multiple response shapes:
      // - { status: { success: boolean, message }, data: ... }
      // - { success: boolean, message: string }
      if (data && typeof data === 'object') {
        if (data.status && typeof data.status.success !== 'undefined') {
          if (data.status.success) {
            return { success: true, message: data.status.message || 'Đặt lại mật khẩu thành công', code: data.status.code };
          }
          return { success: false, message: data.status.message || data.message || 'Có lỗi xảy ra', code: data.status.code };
        }

        if (typeof data.success !== 'undefined') {
          if (data.success) {
            return { success: true, message: data.message || 'Đặt lại mật khẩu thành công', code: data.code };
          }
          return { success: false, message: data.message || 'Có lỗi xảy ra', code: data.code };
        }
      }

      return { success: false, message: 'Có lỗi xảy ra' };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi đặt lại mật khẩu',
      };
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
    try {
      const token = this.getAccessToken();
      if (!token) {
        return {
          success: false,
          message: 'Vui lòng đăng nhập để đổi mật khẩu',
        };
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };

      const response = await fetch(`${this.baseURL}/v1/auth/change-password`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ oldPassword, password: newPassword }),
        credentials: 'include',
      });

      const data = await response.json();
      
      if (data.status && data.status.success) {
        return {
          success: true,
          message: 'Đổi mật khẩu thành công',
        };
      } else {
        return {
          success: false,
          message: data.status?.message || 'Có lỗi xảy ra khi đổi mật khẩu',
        };
      }
    } catch (error) {
      console.error('Change password error:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi đổi mật khẩu',
      };
    }
  }

  // Utility methods for token management
  getAccessToken(): string | null {
    return this.getCookie('accessToken');
  }

  setAccessToken(token: string, days: number = 7): void {
    this.setCookie('accessToken', token, days);
  }

  removeAccessToken(): void {
    this.removeCookie('accessToken');
  }

  // Cookie utility methods
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;secure;samesite=strict`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  private removeCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }
}

export const authService = new AuthService();
export default authService;
